// Copyright 2022 Carnegie Mellon University. All Rights Reserved.
// Released under a MIT (SEI)-style license. See LICENSE.md in the
// project root for license information.
import { Component, Input, OnDestroy, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Sort } from '@angular/material/sort';
import { Subject, Subscription, Observable, of } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import {
  takeUntil,
  debounceTime,
  distinctUntilChanged,
  mergeMap,
  delay,
} from 'rxjs/operators';
import { ComnSettingsService, Theme } from '@cmusei/crucible-common';
import {
  Card,
  Competency,
  CompetencyFramework,
  DataField,
  DataValue,
  Move,
  Msel,
  MselCompetency,
  Organization,
  ScenarioEvent,
  Team,
  User,
} from 'src/app/generated/blueprint.api';
import { MselDataService } from 'src/app/data/msel/msel-data.service';
import { MselQuery } from 'src/app/data/msel/msel.query';
import { MatMenuTrigger } from '@angular/material/menu';
import { CardQuery } from 'src/app/data/card/card.query';
import { DataValueDataService } from 'src/app/data/data-value/data-value-data.service';
import { DataValueQuery } from 'src/app/data/data-value/data-value.query';
import { TeamQuery } from 'src/app/data/team/team.query';
import { DataFieldDataService } from 'src/app/data/data-field/data-field-data.service';
import { v4 as uuidv4 } from 'uuid';
import { DataFieldQuery } from 'src/app/data/data-field/data-field.query';
import { MoveQuery } from 'src/app/data/move/move.query';
import { OrganizationQuery } from 'src/app/data/organization/organization.query';
import {
  DataValuePlus,
  ScenarioEventDataService,
  ScenarioEventView,
  ScenarioEventViewIndexing,
} from 'src/app/data/scenario-event/scenario-event-data.service';
import { ScenarioEventQuery } from 'src/app/data/scenario-event/scenario-event.query';
import { MselCompetencyQuery } from 'src/app/data/msel-competency/msel-competency.query';
import { MselCompetencyDataService } from 'src/app/data/msel-competency/msel-competency-data.service';
import { CompetencyFrameworkQuery } from 'src/app/data/competency-framework/competency-framework.query';
import { CompetencyFrameworkDataService } from 'src/app/data/competency-framework/competency-framework-data.service';
import { UIDataService } from 'src/app/data/ui/ui-data.service';
import { VIEW_CONFIG } from 'src/app/utilities/editor-config';

@Component({
    selector: 'app-msel-view',
    templateUrl: './msel-view.component.html',
    styleUrls: ['./msel-view.component.scss'],
    standalone: false
})
export class MselViewComponent implements OnDestroy, ScenarioEventView {
  @Input() tabHeight: number;
  @Input() userTheme: Theme;
  // context menu
  @ViewChild(MatMenuTrigger, { static: true }) contextMenu: MatMenuTrigger;
  contextMenuPosition = { x: '0px', y: '0px' };
  myTopHeight = 79;
  msel: Msel = { name: '' };
  expandedScenarioEventIds: string[] = [];
  expandedMoreScenarioEventIds: string[] = [];
  showSearch = false;

  // ScenarioEventView Fields
  mselScenarioEvents: ScenarioEvent[] = [];
  filterString = '';
  sort: Sort = { active: '', direction: '' };
  sortedScenarioEvents: ScenarioEvent[];
  sortedDataFields: DataField[];
  dataValues: DataValue[] = [];
  cardList: Card[] = [];
  mselUsers: User[] = [];
  viewIndex = new ScenarioEventViewIndexing();

  sortableDataTypes = this.scenarioEventDataService.sortableDataTypes;
  names: string[] = [];
  organizationList: Organization[] = [];
  blankDataValue = {
    id: '',
    scenarioEventId: '',
    dataFieldId: '',
    value: '',
    valueArray: [],
  } as DataValuePlus;
  darkThemeTint = this.settingsService.settings.DarkThemeTint
    ? this.settingsService.settings.DarkThemeTint
    : 0.7;
  lightThemeTint = this.settingsService.settings.LightThemeTint
    ? this.settingsService.settings.LightThemeTint
    : 0.4;
  showRealTime = false;
  moveAndGroupNumbers: Record<string, number[]>[] = [];
  moveList: Move[] = [];
  teamList: Team[] = [];
  filterChange = new Subject<string>();
  private subscription: Subscription;
  private unsubscribe$ = new Subject();
  private competencyCache = new Map<string, Competency>();
  private competencyTypeCache = new Map<string, string>();
  viewConfig = VIEW_CONFIG;

  constructor(
    private activatedRoute: ActivatedRoute,
    private mselDataService: MselDataService,
    private mselQuery: MselQuery,
    private organizationQuery: OrganizationQuery,
    private dataValueDataService: DataValueDataService,
    private dataValueQuery: DataValueQuery,
    private teamQuery: TeamQuery,
    private dataFieldDataService: DataFieldDataService,
    private dataFieldQuery: DataFieldQuery,
    private cardQuery: CardQuery,
    private moveQuery: MoveQuery,
    private scenarioEventDataService: ScenarioEventDataService,
    private scenarioEventQuery: ScenarioEventQuery,
    private mselCompetencyQuery: MselCompetencyQuery,
    private mselCompetencyDataService: MselCompetencyDataService,
    private competencyFrameworkQuery: CompetencyFrameworkQuery,
    private competencyFrameworkDataService: CompetencyFrameworkDataService,
    private uiDataService: UIDataService,
    private http: HttpClient,
    private settingsService: ComnSettingsService
  ) {
    // subscribe to the route parameters.  Used when viewing independently.
    this.activatedRoute.params
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((params) => {
        const mselId = params['mselid'];
        if (mselId) {
          this.mselDataService.loadById(mselId);
          this.loadInitialData(mselId);
          this.mselDataService.setActive(mselId);
          // Call xAPI for viewed event (only on view page, not build page)
          const baseUrl = this.settingsService.settings.ApiUrl.endsWith('/')
            ? this.settingsService.settings.ApiUrl
            : this.settingsService.settings.ApiUrl + '/';
          this.http.post(`${baseUrl}api/xapi/viewed/msel/${mselId}`, {}).subscribe();
        }
      });
    // subscribe to the route query parameters.  Used when editing the MSEL and checking the view.
    activatedRoute.queryParamMap
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((params) => {
        // set the selected tab based on the injectData
        const mselId = params.get('msel');
        if (!mselId) {
          this.mselDataService.setActive('');
        } else if (!this.msel || this.msel.id !== mselId) {
          this.loadInitialData(mselId);
          this.mselDataService.setActive(mselId);
        }
      });
    // subscribe to the active MSEL
    (this.mselQuery.selectActive() as Observable<Msel>)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((msel) => {
        if (msel) {
          this.msel = { ...msel };
          this.mselUsers = this.getMselUsers();
        }
      });
    // subscribe to data fields
    this.dataFieldQuery
      .selectAll()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((dataFields) => {
        this.sortedDataFields = this.getSortedDataFields(dataFields);
        this.scenarioEventDataService.updateScenarioEventViewDataFields(this);
        this.scenarioEventDataService.updateScenarioEventViewDataValues(this);
        this.scenarioEventDataService.updateScenarioEventViewDisplayedEvents(
          this
        );
      });
    // subscribe to data values
    this.dataValueQuery
      .selectAll()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((dataValues) => {
        this.dataValues = [];
        dataValues.forEach((dv) => {
          this.dataValues.push({ ...dv });
        });
        this.scenarioEventDataService.updateScenarioEventViewDataValues(this);
        this.scenarioEventDataService.updateScenarioEventViewDisplayedEvents(
          this
        );
      });
    // subscribe to scenario events
    this.scenarioEventQuery
      .selectAll()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((scenarioEvents) => {
        this.scenarioEventDataService.refreshScenarioEventViewEvents(
          this,
          scenarioEvents
        );
        if (scenarioEvents && scenarioEvents.length > 0) {
          this.moveAndGroupNumbers =
            this.scenarioEventDataService.getMoveAndGroupNumbers(
              this.mselScenarioEvents,
              this.moveList
            );
          this.scenarioEventDataService.updateScenarioEventViewDisplayedEvents(
            this
          );
        }
      });
    // subscribe to organizations
    this.organizationQuery
      .selectAll()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((organizations) => {
        this.organizationList = organizations.filter(
          (org) => !org.isTemplate && org.mselId === this.msel.id
        );
      });
    // observe the Cards
    this.cardQuery
      .selectAll()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((cards) => {
        this.cardList = cards;
      });
    this.scenarioEventDataService.updateScenarioEventViewCards(this);
    this.scenarioEventDataService.updateScenarioEventViewDisplayedEvents(this);
    // observe the Moves
    this.moveQuery
      .selectAll()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((moves) => {
        this.moveList = moves.sort((a, b) =>
          +a.moveNumber < +b.moveNumber ? -1 : 1
        );
        this.moveAndGroupNumbers =
          this.scenarioEventDataService.getMoveAndGroupNumbers(
            this.sortedScenarioEvents,
            this.moveList
          );
      });
    // observe the Teams
    this.teamQuery
      .selectAll()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((teams) => {
        this.teamList = teams;
      });
    // observe the MselCompetencies for tooltip data
    this.mselCompetencyQuery
      .selectAll()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((mselCompetencies) => {
        this.competencyCache.clear();
        for (const mc of mselCompetencies) {
          if (mc.competency && mc.competency.idNumber) {
            this.competencyCache.set(mc.competency.idNumber, mc.competency);
          }
        }
      });
    // subscribe to filter string changes for debounce
    this.subscription = this.filterChange
      .pipe(
        debounceTime(250),
        distinctUntilChanged(),
        mergeMap((search) => of(search).pipe(delay(250)))
      )
      .subscribe((event) => {
        this.applyFilter(this.filterString);
      });
    // set the time display format
    this.showRealTime = this.uiDataService.useRealTime();
  }

  get userList(): User[] {
    return this.mselUsers;
  }

  get dataFields(): DataField[] {
    return this.sortedDataFields;
  }

  get showHiddenEvents(): boolean {
    return false;
  }

  get displayedScenarioEvents(): ScenarioEvent[] {
    return this.sortedScenarioEvents;
  }
  set displayedScenarioEvents(evts: ScenarioEvent[]) {
    this.sortedScenarioEvents = evts;
  }

  loadInitialData(mselId: string) {
    this.dataFieldDataService.loadByMsel(mselId);
    this.dataValueDataService.loadByMsel(mselId);
    this.scenarioEventDataService.loadByMsel(mselId);
    this.mselCompetencyDataService.loadByMsel(mselId);
    this.competencyFrameworkDataService.load();
  }

  applyFilter(filterValue: string) {
    this.filterString = filterValue;
    this.scenarioEventDataService.updateScenarioEventViewDisplayedEvents(this);
  }

  sortChanged(sort: Sort) {
    this.sort = sort;
    this.scenarioEventDataService.updateScenarioEventViewDisplayedEvents(this);
  }

  getSortedDataFields(dataFields: DataField[]): DataField[] {
    this.names = [];
    const sortedDataFields: DataField[] = [];
    if (dataFields) {
      dataFields.forEach((df) => {
        if (df.onExerciseView) {
          sortedDataFields.push({ ...df });
          this.names.push(df.name);
        }
      });
      sortedDataFields.sort((a, b) =>
        +a.displayOrder > +b.displayOrder ? 1 : -1
      );
    }
    return sortedDataFields;
  }

  newDataValuePlus(
    scenarioEventId: string,
    dataFieldId: string
  ): DataValuePlus {
    const ndvp = {
      id: uuidv4(),
      scenarioEventId: scenarioEventId,
      dataFieldId: dataFieldId,
      value: '',
      valueArray: [],
    } as DataValuePlus;

    return ndvp;
  }

  getDataValue(
    scenarioEvent: ScenarioEvent,
    dataFieldName: string
  ): DataValuePlus {
    if (!(this.msel && scenarioEvent && scenarioEvent.id)) {
      return this.blankDataValue;
    }
    return this.scenarioEventDataService.getDataValueFromView(
      this,
      scenarioEvent,
      dataFieldName
    );
  }

  getDisplayValue(scenarioEvent: ScenarioEvent, dataFieldName: string): string {
    if (!(this.msel && scenarioEvent && scenarioEvent.id)) {
      return '';
    }
    return this.scenarioEventDataService.getDisplayValueFromView(
      this,
      scenarioEvent,
      dataFieldName
    );
  }

  getMselUsers(): User[] {
    let users = [];
    this.msel.teams.forEach((team) => {
      team.users.forEach((user) => {
        users.push({ ...user });
      });
    });
    this.msel.units.forEach((unit) => {
      unit.users.forEach((user) => {
        users.push({ ...user });
      });
    });
    users = users.sort((a, b) =>
      a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1
    );
    return users;
  }

  getCompetencyTooltip(idNumber: string): string {
    const comp = this.competencyCache.get(idNumber);
    return comp?.shortName || '';
  }

  private getCompetencyType(comp: Competency): string {
    if (!comp || !comp.id) return '';

    // Check cache first
    if (this.competencyTypeCache.has(comp.id)) {
      return this.competencyTypeCache.get(comp.id) || '';
    }

    // Derive from ID pattern
    const idNumber = comp.idNumber || '';
    let type = '';

    if (idNumber.includes('WRL')) {
      type = 'Work Role';
    } else if (/^[TKSA][\d-]/.test(idNumber)) {
      const prefixMap: Record<string, string> = {
        'T': 'Task', 'K': 'Knowledge', 'S': 'Skill', 'A': 'Ability',
      };
      type = prefixMap[idNumber.charAt(0)] || '';
    } else if (/^[A-Z]{2}-[A-Z]{3}-\d+$/.test(idNumber)) {
      type = 'Work Role';
    } else if (/^[A-Z]{2}-\d+[A-Z]?$/.test(idNumber)) {
      type = 'Work Role';
    } else if (/^[A-Z]{3}$/.test(idNumber)) {
      type = 'Specialty Area';
    } else if (/^[A-Z]{2}$/.test(idNumber)) {
      type = 'Category';
    }

    this.competencyTypeCache.set(comp.id, type);
    return type;
  }

  trackByFn(index, item) {
    return item.id;
  }

  getSortedOrganizationOptions(): string[] {
    let orgs: string[] = [];
    this.organizationList.forEach((o) => {
      orgs.push(o.shortName);
    });
    this.msel.teams.forEach((t) => {
      orgs.push(t.shortName);
    });
    orgs = orgs.sort((a, b) => (a < b ? -1 : 1));
    return orgs;
  }

  getSortedTeamOptions(): string[] {
    let teams: string[] = [];
    this.msel.teams.forEach((t) => {
      teams.push(t.shortName);
    });
    teams = teams.sort((a, b) => (a < b ? -1 : 1));
    return teams;
  }

  getScrollingHeight() {
    const topHeight = this.tabHeight
      ? this.myTopHeight + this.tabHeight
      : this.myTopHeight;
    return 'calc(100% - ' + topHeight + 'px)';
  }

  blankDataValuePlus(): DataValuePlus {
    const bdvp = {
      id: '',
      scenarioEventId: '',
      dataFieldId: '',
      value: '',
      valueArray: [],
    } as DataValuePlus;

    return bdvp;
  }

  saveDataValue(
    scenarioEvent: ScenarioEvent,
    dataField: DataField,
    newValue: string
  ) {
    let dataValue = this.getDataValue(scenarioEvent, dataField.name);
    if (!dataValue || !dataValue.id) {
      // the dataValue doesn't exist, so create a new one
      const dataFieldId = this.sortedDataFields.find(
        (df) => df.name === dataField.name
      ).id;
      dataValue = this.newDataValuePlus(scenarioEvent.id, dataFieldId);
      dataValue.value = newValue;
      this.dataValueDataService.add(dataValue);
    } else {
      dataValue.value = newValue;
      this.dataValueDataService.updateDataValue(dataValue);
    }
  }

  getRowStyle(scenarioEvent: ScenarioEvent) {
    if (!scenarioEvent || !scenarioEvent.rowMetadata) {
      return '';
    }
    const rowMetadata = scenarioEvent.rowMetadata
      ? scenarioEvent.rowMetadata.split(',')
      : [];
    const color =
      rowMetadata.length >= 4
        ? rowMetadata[1] + ', ' + rowMetadata[2] + ', ' + rowMetadata[3]
        : '';
    const tint =
      this.userTheme === 'dark-theme'
        ? this.darkThemeTint
        : this.lightThemeTint;
    const style = color
      ? { 'background-color': 'rgba(' + color + ', ' + tint + ')' }
      : {};
    return style;
  }

  getStyle(dataField: DataField): string {
    if (dataField && dataField.columnMetadata) {
      const width = Math.trunc(+dataField.columnMetadata * 7); // 7 converts excel widths to http widths
      return 'text-align: left; width: ' + width.toString() + 'px;';
    } else if (dataField.dataType.toString() === 'DateTime') {
      return 'width: max-content';
    } else {
      return 'text-align: left; width: 90%; min-width: 40px;';
      // return 'width: 100%;';
    }
  }

  setSearch(value: boolean) {
    if (!value) {
      this.applyFilter('');
    }
    this.showSearch = value;
  }

  setRealTime(value: boolean) {
    this.showRealTime = value;
    this.uiDataService.setUseRealTime(value);
  }

  shouldShowMoveSeparators(): boolean {
    if (!this.sort || !this.sort.active || !this.sort.direction) return true;
    if (this.sort.direction !== 'asc') return false;
    return this.sort.active === 'moveNumber' || this.sort.active === 'deltaSeconds';
  }

  isMoveStartRow(rowIndex: number): boolean {
    if (!this.shouldShowMoveSeparators()) return false;
    const events = this.sortedScenarioEvents;
    if (!events || !events[rowIndex]) return false;
    const currMove = this.moveAndGroupNumbers[events[rowIndex].id]?.[0];
    if (currMove == null || currMove < 0) return false;
    if (rowIndex === 0) return true;
    const prevMove = this.moveAndGroupNumbers[events[rowIndex - 1].id]?.[0];
    return currMove !== prevMove;
  }

  getMoveLabel(rowIndex: number): string {
    const events = this.sortedScenarioEvents;
    const moveNum = this.moveAndGroupNumbers[events[rowIndex].id]?.[0];
    const move = this.moveList.find((m) => +m.moveNumber === +moveNum);
    const desc = move?.description?.trim();
    return desc ? `Start Move ${moveNum} — ${desc}` : `Start Move ${moveNum}`;
  }

  ngOnDestroy() {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }
}
